export function isSafariAudioBrowser(userAgent: string): boolean {
  return /Safari\//.test(userAgent) && !/Chrome\/|Chromium\/|CriOS\/|FxiOS\/|Edg\/|EdgiOS\/|OPR\/|OPiOS\//.test(userAgent);
}

/** PCM WAV is reliably playable by Safari's HTML audio element. */
export function encodePcmWav(buffer: AudioBuffer): Blob {
  const channels = buffer.numberOfChannels;
  const byteLength = buffer.length * channels * 2;
  const bytes = new ArrayBuffer(44 + byteLength);
  const view = new DataView(bytes);
  const text = (offset: number, value: string) => {
    for (let i = 0; i < value.length; i++) view.setUint8(offset + i, value.charCodeAt(i));
  };
  text(0, "RIFF");
  view.setUint32(4, 36 + byteLength, true);
  text(8, "WAVE");
  text(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, channels, true);
  view.setUint32(24, buffer.sampleRate, true);
  view.setUint32(28, buffer.sampleRate * channels * 2, true);
  view.setUint16(32, channels * 2, true);
  view.setUint16(34, 16, true);
  text(36, "data");
  view.setUint32(40, byteLength, true);
  const samples = Array.from({ length: channels }, (_, channel) => buffer.getChannelData(channel));
  let offset = 44;
  for (let frame = 0; frame < buffer.length; frame++) {
    for (const channel of samples) {
      const sample = Math.max(-1, Math.min(1, channel[frame] ?? 0));
      view.setInt16(offset, Math.round(sample * (sample < 0 ? 32768 : 32767)), true);
      offset += 2;
    }
  }
  return new Blob([bytes], { type: "audio/wav" });
}

/** Only the local playback copy changes; uploads retain their original bytes. */
export async function safariPlaybackBlob(blob: Blob, signal: AbortSignal): Promise<Blob> {
  if (!isSafariAudioBrowser(navigator.userAgent) || !/^audio\/webm(?:;|$)/i.test(blob.type)) return blob;
  signal.throwIfAborted();
  const context = new AudioContext();
  const close = () => { void context.close().catch(() => {}); };
  signal.addEventListener("abort", close, { once: true });
  try {
    const bytes = await blob.arrayBuffer();
    signal.throwIfAborted();
    const audio = await context.decodeAudioData(bytes);
    signal.throwIfAborted();
    return encodePcmWav(audio);
  } finally {
    signal.removeEventListener("abort", close);
    if (context.state !== "closed") await context.close().catch(() => {});
  }
}
